import { FC, useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import useTheme from '@mui/material/styles/useTheme';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import RadioButtonChecked from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import { Text } from '@concepta/react-material-ui';
import { isMobile } from 'utils/isMobile';
import { alpha } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import { ResortInterface } from 'ks-common';

export interface UserEnteredAddress {
  streetAddress: string;
  city: string;
  zip: string;
}

export interface ResortIdentifiedModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (resort: ResortInterface) => void;
  onContactSupport: () => void;
  matchedResorts: ResortInterface[];
  userEnteredAddress: UserEnteredAddress;
}

interface SelectableAddressCardProps {
  title: string;
  address: string;
  isSelected: boolean;
  onClick: () => void;
  isResort?: boolean;
  id: string;
}

const SelectableAddressCard: FC<SelectableAddressCardProps> = ({
  title,
  address,
  isSelected,
  onClick,
  isResort = true,
  id,
}) => {
  const theme = useTheme();

  return (
    <Box
      id={id}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="radio"
      aria-checked={isSelected}
      aria-label={`${title}, ${address}`}
      tabIndex={0}
      sx={{
        border: `2px solid ${
          isSelected ? theme.palette.primary.main : theme.palette.grey[300]
        }`,
        borderRadius: '12px',
        p: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': {
          borderColor: isSelected
            ? theme.palette.primary.main
            : theme.palette.grey[400],
          bgcolor: isSelected ? undefined : alpha(theme.palette.grey[100], 0.5),
        },
      }}
    >
      <Box lineHeight={0} flexShrink={0}>
        {isSelected ? (
          <RadioButtonChecked sx={{ fontSize: 24, color: 'primary.main' }} />
        ) : (
          <RadioButtonUnchecked sx={{ fontSize: 24, color: 'text.disabled' }} />
        )}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={40}
        height={40}
        borderRadius="8px"
        bgcolor={
          isSelected
            ? alpha(theme.palette.primary.main, 0.1)
            : theme.palette.grey[100]
        }
        flexShrink={0}
      >
        {isResort ? (
          <BusinessOutlined
            sx={{
              fontSize: 24,
              color: isSelected ? 'primary.main' : 'text.secondary',
            }}
          />
        ) : (
          <HomeOutlined
            sx={{
              fontSize: 24,
              color: isSelected ? 'primary.main' : 'text.secondary',
            }}
          />
        )}
      </Box>
      <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
        <Text
          fontSize={14}
          fontWeight={600}
          color={isSelected ? 'primary.main' : 'text.primary'}
          sx={{ wordBreak: 'break-word' }}
        >
          {title}
        </Text>
        <Text
          fontSize={12}
          fontWeight={400}
          color="text.secondary"
          sx={{ wordBreak: 'break-word' }}
        >
          {address}
        </Text>
      </Box>
    </Box>
  );
};

const ResortIdentifiedModal: FC<ResortIdentifiedModalProps> = ({
  open,
  onClose,
  onConfirm,
  onContactSupport,
  matchedResorts,
  userEnteredAddress,
}) => {
  const theme = useTheme();
  const [selectedResortId, setSelectedResortId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedResortId(
      matchedResorts.length === 1 ? matchedResorts[0].ResortId : null,
    );
  }, [matchedResorts]);

  if (!matchedResorts || matchedResorts.length === 0) return null;

  const selectedResort = matchedResorts.find(
    r => r.ResortId === selectedResortId,
  );

  const handleConfirm = () => {
    if (selectedResort) {
      onConfirm(selectedResort);
    }
  };

  const formatResortAddress = (resort: ResortInterface) => {
    const parts = [
      resort.Street,
      resort.City,
      [resort.State, resort.ZIP].filter(Boolean).join(' '),
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatUserAddress = (addr: UserEnteredAddress) => {
    const parts = [
      addr.streetAddress,
      [addr.city, addr.zip].filter(Boolean).join(' '),
    ].filter(Boolean);
    return parts.join(', ');
  };

  const hasMultipleMatches = matchedResorts.length > 1;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      aria-labelledby="resort-modal-title"
      aria-describedby="resort-modal-description"
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
    >
      <Box position="absolute" right={16} top={16}>
        <IconButton edge="start" onClick={onClose} aria-label="close">
          <CloseOutlined
            sx={{
              color: 'primary.darker',
            }}
          />
        </IconButton>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={isMobile ? 2 : 3}
        gap={3}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={64}
          height={64}
          borderRadius="50%"
          bgcolor={alpha(theme.palette.primary.main, 0.1)}
        >
          <BusinessOutlined sx={{ fontSize: 32, color: 'primary.main' }} />
        </Box>

        <Text
          id="resort-modal-title"
          fontSize={20}
          fontWeight={700}
          color="primary.darker"
          textAlign="center"
        >
          {hasMultipleMatches
            ? 'Multiple resorts identified'
            : 'Resort address identified'}
        </Text>

        <Text
          id="resort-modal-description"
          fontSize={14}
          fontWeight={400}
          color="text.secondary"
          textAlign="center"
          px={isMobile ? 0 : 2}
        >
          The address you provided as a vacation home corresponds to a resort in
          our database. To ensure that we deliver your order to the correct
          location, please confirm that the address aligns with the resort
          listed below.
        </Text>

        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          gap={2}
          role="radiogroup"
          aria-label="Address selection"
        >
          {matchedResorts.map(resort => (
            <SelectableAddressCard
              key={resort.ResortId}
              id={`resort-option-${resort.ResortId}`}
              title={resort.ResortName}
              address={formatResortAddress(resort)}
              isSelected={selectedResortId === resort.ResortId}
              onClick={() => setSelectedResortId(resort.ResortId)}
              isResort={true}
            />
          ))}

          <SelectableAddressCard
            id="keep-address-option"
            title="Keep my entered address"
            address={formatUserAddress(userEnteredAddress)}
            isSelected={selectedResortId === null}
            onClick={() => setSelectedResortId(null)}
            isResort={false}
          />
        </Box>

        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          <Text fontSize={14} fontWeight={600} color="text.primary">
            What you can do:
          </Text>

          <Box display="flex" gap={2} alignItems="flex-start">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={32}
              height={32}
              borderRadius="50%"
              bgcolor={alpha(theme.palette.secondary.main, 0.1)}
              flexShrink={0}
            >
              <SupportAgentOutlined
                sx={{ fontSize: 18, color: 'secondary.main' }}
              />
            </Box>
            <Box display="flex" flexDirection="column">
              <Text
                fontSize={theme.typography.body2.fontSize}
                fontWeight={500}
                color="text.primary"
              >
                Contact support
              </Text>
              <Text
                fontSize={theme.typography.caption.fontSize}
                fontWeight={400}
                color="text.secondary"
              >
                Our team can help with alternative solutions
              </Text>
            </Box>
          </Box>
        </Box>

        <Box width="100%" display="flex" flexDirection="column" gap={2} mt={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleConfirm}
            disabled={selectedResortId === null}
          >
            CONFIRM RESORT
          </Button>

          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={onContactSupport}
            startIcon={<SupportAgentOutlined />}
          >
            CONTACT SUPPORT
          </Button>

          <Button variant="outlined" fullWidth onClick={onClose}>
            CLOSE AND SELECT OTHER ADDRESS
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ResortIdentifiedModal;
